import React, {
  useEffect, useState,
} from 'react';

import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { IconButton, Stack } from '@mui/material';
import dayjs, { Dayjs } from 'dayjs';
import isoWeek from 'dayjs/plugin/isoWeek';
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter';
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore';
import { useResizeDetector } from 'react-resize-detector';

import addDays from '../../utils/common/addDays';

import {
  ANIMATION_TIME_SEC,
  CalendarContainer,
  CalendarEntryContainer,
  CalendarToolbar,
  DateHeader,
  DateText,
  NoDataContainer,
  RootContainer,
  Subtitle,
  TodayButton,
} from './index.styles';
import { AnimateType, CalendarEntry } from './types';
import {
  isDateToday, splitEntriesByDay,
} from './utils';

// Allow isoWeek functions
dayjs.extend(isoWeek);
dayjs.extend(isSameOrBefore);
dayjs.extend(isSameOrAfter);

const TODAY = dayjs().startOf('day');

interface CalendarColumnProps {
  datetime: number,
  renderContent: (entry: CalendarEntry<any>) => React.ReactElement | React.ReactElement[],
  contents?: CalendarEntry<any>[],
  columnCount: number,
  highlightToday?: boolean,
}

const CalendarColumn = (
  { datetime,
    renderContent,
    contents = [],
    columnCount,
    highlightToday = true }: CalendarColumnProps,
) => {
  const date = dayjs(datetime);
  const isToday = isDateToday(datetime);

  return (
    <CalendarEntryContainer $columnCount={columnCount} $isToday={isToday && highlightToday}>
      <DateHeader>
        <DateText variant="overline" $isToday={isToday && highlightToday}>
          {date.format('DD/MM')}
        </DateText>
        <Subtitle variant="subtitle2" $isToday={isToday && highlightToday}>
          {date.format('ddd').toUpperCase()}
        </Subtitle>
      </DateHeader>
      <Stack
        direction="column"
        spacing={1}
        justifyContent="center"
        alignItems="center"
        width="90%"
      >
        {contents.map(renderContent)}
      </Stack>
    </CalendarEntryContainer>
  );
};

interface WeekCalendarProps {
  data: CalendarEntry<any>[],
  renderContent: (entry: CalendarEntry<any>) => React.ReactElement | React.ReactElement[],
  onNavigate?: (startDatetime: Dayjs) => void,
  minDate?: Dayjs,
  setMinDate?: (minDate: Dayjs) => void,
  maxDate?: Dayjs,
  setMaxDate?: (maxDate: Dayjs) => void,
}

const WeekCalendar = ({
  data = [],
  renderContent,
  onNavigate = () => {},
  minDate,
  setMinDate,
  maxDate,
  setMaxDate,
}: WeekCalendarProps) => {
  const { width = 0, ref } = useResizeDetector();

  const [animate, setAnimate] = useState<AnimateType>(AnimateType.None);

  const [columnCount, setColumnCount] = useState<number>(width > 960 ? 7 : Math.ceil(width / 144));
  const [visibleDays, setVisibleDays] = useState<number[]>([]);
  const [startDatetime, setStartDatetime] = useState<Dayjs>(TODAY.startOf('isoWeek'));

  const [splitData, setSplitData] = useState<Record<number, CalendarEntry<any>[]>>({});

  /**
   * HELPER FUNCTIONS
   */

  // Updates the visible days
  const updateVisibleDays = () => {
    setVisibleDays(Array.from(Array(columnCount).keys()).map(
      (x) => addDays(startDatetime, x).valueOf()),
    );
  };

  // Returns the number of events currently visible
  const getVisibleCellCount = () => {
    let count = 0;
    visibleDays.forEach((datetime) => {
      if (splitData[Number(datetime)]) count += (splitData[Number(datetime)]).length;
    });
    return count;
  };

  /**
   * ANIMATIONS
   */

  const animateSlide = (type: AnimateType) => {
    setAnimate(type);
    setTimeout(() => setAnimate(AnimateType.None), ANIMATION_TIME_SEC * 1000);
  };

  // Pushes the calendar range forward by the number of visible columns
  const incrementStart = () => {
    const startOfNextRange = addDays(startDatetime, columnCount);
    if (!maxDate || startOfNextRange.isBefore(addDays(maxDate, -columnCount))) {
      setStartDatetime(startOfNextRange);
      animateSlide(AnimateType.Right);
    } else if (setMaxDate) {
      // Fetch another 2 weeks of data
      setMaxDate(addDays(maxDate, 2 * 7));
      setStartDatetime(startOfNextRange);
      animateSlide(AnimateType.Right);
    } else {
      animateSlide(AnimateType.Shake);
    }
  };

  // Pushes the calendar range backward by the number of visible columns
  const decrementStart = () => {
    const startOfPrevRange = addDays(startDatetime, -columnCount);
    if (!minDate || startOfPrevRange.isAfter(minDate)) {
      setStartDatetime(startOfPrevRange);
      animateSlide(AnimateType.Left);
    } else if (setMinDate) {
      // Fetch another 2 weeks of data
      setMinDate(addDays(minDate, -2 * 7));
      setStartDatetime(startOfPrevRange);
      animateSlide(AnimateType.Left);
    } else {
      animateSlide(AnimateType.Shake);
    }
  };

  // Reset the calendar range to begin from the current day
  const resetStartDatetime = (datetime: Dayjs = TODAY) => {
    setStartDatetime(datetime);
    if (datetime.isSameOrBefore(addDays(startDatetime, -(columnCount - 1)))) {
      animateSlide(AnimateType.Left);
    } else if (datetime.isSameOrAfter(addDays(startDatetime, columnCount))) {
      animateSlide(AnimateType.Right);
    }
  };

  // Set initial date to the current day if the column count is less than 7
  // and the Monday of the current week if the column count is 7 or more
  const resetViewWindowToToday = () => {
    if (columnCount >= 7) {
      // Use the Monday
      resetStartDatetime(TODAY.startOf('isoWeek'));
    } else {
      // Use today
      resetStartDatetime(TODAY);
    }
  };

  /**
   * useEffects
   */

  // Set initial start date
  useEffect(() => {
    updateVisibleDays();
  }, []);

  // Set up default view window
  useEffect(() => {
    const isTodayVisible = addDays(startDatetime, 7).isAfter(TODAY);
    if (isTodayVisible && columnCount > 0) {
      // columnCount > 0 is checked, as the component is rendered before the width is calculated
      // and the columnCount is 0 pre-render
      resetViewWindowToToday();
    }

    updateVisibleDays();
  }, [columnCount]);

  useEffect(() => {
    // Use onNavigate to request new data from APIs
    onNavigate(startDatetime);

    updateVisibleDays();
  }, [startDatetime]);

  // Handle window resize
  useEffect(() => {
    setColumnCount(width > 960 ? 7 : Math.ceil(width / 144));
  }, [width]);

  // Reformat the data into per-day basis
  useEffect(() => {
    setSplitData(splitEntriesByDay(data));
  }, [data]);

  return (
    <RootContainer ref={ref as React.RefObject<HTMLDivElement>}>
      <CalendarToolbar>
        <TodayButton
          variant="outlined"
          onClick={resetViewWindowToToday}
          color="gray"
          disableRipple
        >
          TODAY
        </TodayButton>
        <IconButton onClick={decrementStart}>
          <ChevronLeftIcon />
        </IconButton>
        <IconButton onClick={incrementStart}>
          <ChevronRightIcon />
        </IconButton>
      </CalendarToolbar>
      <CalendarContainer animate={animate}>
        <Stack direction="row" width="100%">
          {visibleDays.map((datetime) => (
            <CalendarColumn
              key={datetime}
              highlightToday={getVisibleCellCount() > 0}
              datetime={Number(datetime)}
              renderContent={renderContent}
              contents={splitData[Number(datetime)]}
              columnCount={columnCount}
            />
          ))}
        </Stack>
        {getVisibleCellCount() === 0 && (
          <NoDataContainer variant="body2" color="textSecondary">
            No upcoming events
          </NoDataContainer>
        )}
      </CalendarContainer>
    </RootContainer>
  );
};

export default WeekCalendar;
